import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, useFieldArray } from "react-hook-form";
import { FaPlus, FaTrash, FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import {
  DECIMAL_RE,
  handleDecimalKeyDown,
  handleDecimalPaste,
  handleIntegerBeforeInput,
  handleIntegerKeyDown,
  handleIntegerPaste,
} from "../../config/fieldsHelper";

/** Default diamond row (all numbers as strings for RHF) */
const defaultDiamondFields = {
  diaPopupName: "",
  // meta (optional; useful to prefill from master)
  shape: "",
  color: "",
  clarity: "",
  sieve: "",
  // inputs
  diaPopupWt: "",          // carat
  diaPopupRateByCt: "",    // rate per carat
  diaPopupPcs: "",
  diaPopupRateByPcs: "",
  diaPopupAmt: "",
  diaPopupDeduct: "",      // deduct | add | nonDeduct
  diaPopupDescription: "",
};

const InvoiceDiamondPopUp = ({
  initialDiamonds = [],
  setOpenDiamondModal,
  onSave,
  onClose,
}) => {
  // Assuming diamonds master in store at s.diamonds?.diamonds
  const diamondsData = useSelector((s) => s.diamonds?.diamonds);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    setFocus,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      diamonds:
        initialDiamonds?.length ? initialDiamonds : [defaultDiamondFields],
    },
    shouldUnregister: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "diamonds",
  });

  // refs
  const rowRefs = useRef([]);
  const firstSelectRef = useRef(null);
  const panelRef = useRef(null);
  const prevInitRef = useRef(null);

  // 🔒 Focus control (one-shot)
  const focusArmedRef = useRef(true);
  const tryFocusRHF = () => {
    try {
      setFocus("diamonds.0.diaPopupName");
      return true;
    } catch {
      return false;
    }
  };
  const tryFocusDOM = () => {
    const el =
      firstSelectRef.current ||
      document.querySelector('select[name="diamonds.0.diaPopupName"]');
    if (!el) return false;
    if (typeof el.focus === "function") {
      el.focus({ preventScroll: true });
      rowRefs.current?.[0]?.scrollIntoView({
        behavior: "instant",
        block: "center",
      });
      return document.activeElement === el;
    }
    return false;
  };
  const focusFirst = () => {
    if (!focusArmedRef.current) return true;
    const a = tryFocusRHF();
    const b = tryFocusDOM();
    const ok = a || b;
    if (ok) focusArmedRef.current = false;
    return ok;
  };
  const focusWithRetries = (tries = 10, delay = 40) => {
    if (!focusArmedRef.current) return;
    let count = 0;
    const tick = () => {
      if (!focusArmedRef.current) return;
      if (focusFirst() || count >= tries) return;
      count++;
      setTimeout(tick, delay);
    };
    if (!focusFirst()) setTimeout(tick, delay);
  };

  // Smooth scroll preference while modal open
  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = prev || "";
    };
  }, []);

  // Reset & focus when initialDiamonds changes
  useEffect(() => {
    const curr = JSON.stringify(initialDiamonds || []);
    if (curr !== prevInitRef.current) {
      reset({
        diamonds:
          initialDiamonds?.length ? initialDiamonds : [defaultDiamondFields],
      });
      focusArmedRef.current = true;
      setTimeout(() => focusWithRetries(), 0);
      prevInitRef.current = curr;
    }
  }, [initialDiamonds, reset]);

  // First mount
  useEffect(() => {
    focusArmedRef.current = true;
    focusWithRetries();
  }, []);

  // Focus trap (optional)
  useEffect(() => {
    const root = panelRef.current;
    if (!root) return;

    const getFocusable = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"));

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const f = getFocusable();
      if (!f.length) return;
      const first = f[0],
        last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onFocusCapture = () => {
      focusArmedRef.current = false;
    };

    root.addEventListener("keydown", onKeyDown);
    root.addEventListener("focusin", onFocusCapture);
    return () => {
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("focusin", onFocusCapture);
    };
  }, []);

  // amount recompute: prefer wt*rateByCt if wt present; else pcs*rateByPcs
  const recomputeAmt = (i) => {
    const ct = parseFloat(getValues(`diamonds.${i}.diaPopupWt`)); // carat
    const rCt = parseFloat(getValues(`diamonds.${i}.diaPopupRateByCt`));
    const pcs = parseInt(getValues(`diamonds.${i}.diaPopupPcs`), 10);
    const rPc = parseFloat(getValues(`diamonds.${i}.diaPopupRateByPcs`));
    let amt = "";
    if (!isNaN(ct) && !isNaN(rCt) && (isNaN(pcs) || pcs === 0))
      amt = (ct * rCt).toFixed(2);
    else if (!isNaN(pcs) && !isNaN(rPc)) amt = (pcs * rPc).toFixed(2);
    setValue(`diamonds.${i}.diaPopupAmt`, amt, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const requestClose = () =>
    onClose ? onClose() : setOpenDiamondModal?.(false);

  const doSave = (data) => {
    onSave?.(data?.diamonds || []);
    requestClose();
  };

  // ESC to close
  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        requestClose();
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Backdrop click to close
  const onBackdropMouseDown = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) requestClose();
  };

  const rows = watch("diamonds") || [];
  const totals = {
    totalCt: rows
      .reduce((s, x) => s + (parseFloat(x?.diaPopupWt) || 0), 0)
      .toFixed(3),
    totalPcs: rows.reduce(
      (s, x) => s + (parseInt(x?.diaPopupPcs, 10) || 0),
      0
    ),
    totalAmt: rows
      .reduce((s, x) => s + (parseFloat(x?.diaPopupAmt) || 0), 0)
      .toFixed(2),
  };

  const scrollRowIntoView = (idx) => {
    const el = rowRefs.current?.[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="backdrop"
        className="fixed inset-0 z-50 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={onBackdropMouseDown}
      />
      {/* Panel */}
      <motion.div
        key="panel"
        className="fixed inset-0 z-50 grid place-items-center p-4"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-diamond-title"
        onAnimationComplete={() => focusWithRetries()}
      >
        <div
          ref={panelRef}
          className="w-full max-w-5xl max-h-[260px] rounded-xl border border-violet-300 bg-[#f7f4ff] shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-violet-300 rounded-t-xl px-4 py-2 flex items-center justify-between">
            <h5 id="invoice-diamond-title" className="text-sm font-semibold text-violet-800">
              💎 Diamonds
            </h5>
            <button
              type="button"
              onClick={requestClose}
              className="p-2 text-slate-600 hover:text-slate-800"
              aria-label="Close diamonds popup"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-3 pt-2 pb-16 snap-y snap-mandatory">
            {fields.map((field, idx) => {
              const hasCt = !!watch(`diamonds.${idx}.diaPopupWt`);
              const hasRateCt = !!watch(`diamonds.${idx}.diaPopupRateByCt`);
              const hasPcs = !!watch(`diamonds.${idx}.diaPopupPcs`);
              const hasRatePcs = !!watch(`diamonds.${idx}.diaPopupRateByPcs`);
              const weightMode = hasCt || hasRateCt;
              const pcsMode = hasPcs || hasRatePcs;

              const nameReg = register(`diamonds.${idx}.diaPopupName`, {
                onChange: (e) => {
                  const id = e.target.value;
                  const sel = (diamondsData || []).find(
                    (d) => String(d?._id) === String(id)
                  );
                  if (!sel) return;
                  // Prefill auxiliary fields if present in master
                  setValue(`diamonds.${idx}.shape`, sel?.shape ?? "");
                  setValue(`diamonds.${idx}.color`, sel?.color ?? "");
                  setValue(`diamonds.${idx}.clarity`, sel?.clarity ?? "");
                  setValue(`diamonds.${idx}.sieve`, sel?.sieve ?? "");
                  // If master holds defaults
                  if (sel?.wt != null)
                    setValue(`diamonds.${idx}.diaPopupWt`, sel.wt);
                  if (sel?.rateByCt != null)
                    setValue(`diamonds.${idx}.diaPopupRateByCt`, sel.rateByCt);
                  if (sel?.pcs != null)
                    setValue(`diamonds.${idx}.diaPopupPcs`, sel.pcs);
                  if (sel?.rateByPcs != null)
                    setValue(
                      `diamonds.${idx}.diaPopupRateByPcs`,
                      sel.rateByPcs
                    );
                  if (sel?.amount != null)
                    setValue(`diamonds.${idx}.diaPopupAmt`, sel.amount);
                  if (sel?.deduct)
                    setValue(`diamonds.${idx}.diaPopupDeduct`, sel.deduct);
                  if (sel?.description)
                    setValue(
                      `diamonds.${idx}.diaPopupDescription`,
                      sel.description
                    );
                },
              });

              return (
                <div
                  key={field.id}
                  ref={(el) => (rowRefs.current[idx] = el)}
                  className="snap-center border border-violet-200 bg-white rounded-md p-2 shadow-sm mb-2 relative"
                >
                  <div className="absolute -left-2 -top-2 bg-violet-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center shadow">
                    {idx + 1}
                  </div>

                  <div className="grid grid-cols-6 gap-2 mb-2">
                    {/* Name */}
                    <div>
                      <label htmlFor={`diamond-name-${idx}`} className="text-[10px] text-gray-600">
                        Name
                      </label>
                      <div className="rounded-md border border-gray-300 focus-within:ring-2 focus-within:ring-violet-400 focus-within:border-violet-400 transition-shadow">
                        <select
                          {...nameReg}
                          id={`diamond-name-${idx}`}
                          ref={(el) => {
                            nameReg.ref(el);
                            if (idx === 0) {
                              firstSelectRef.current = el;
                              if (el && focusArmedRef.current) {
                                setTimeout(() => {
                                  if (focusArmedRef.current) {
                                    el.focus({ preventScroll: true });
                                    focusArmedRef.current = false;
                                  }
                                }, 0);
                              }
                            }
                          }}
                          autoComplete="off"
                          name={`diamonds.${idx}.diaPopupName`}
                          className="w-full bg-white px-2 py-0.5 text-xs rounded-md focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                          tabIndex={0}
                        >
                          <option value="">--Select--</option>
                          {diamondsData?.map((d) => (
                            <option key={d._id} value={d._id}>
                              {d.diamondName || d.name || "Diamond"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Wt (carat) */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Wt (ct)
                      </label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) =>
                            v === "" ||
                            DECIMAL_RE.test(String(v)) ||
                            "Only numbers",
                        })}
                        placeholder="Ct"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(
                            e,
                            watch(`diamonds.${idx}.diaPopupWt`) ?? ""
                          )
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Ct */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Rate/Ct
                      </label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupRateByCt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) =>
                            v === "" ||
                            DECIMAL_RE.test(String(v)) ||
                            "Only numbers",
                        })}
                        placeholder="Rate/Ct"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(
                            e,
                            watch(`diamonds.${idx}.diaPopupRateByCt`) ?? ""
                          )
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Pcs</label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupPcs`, {
                          onChange: () => recomputeAmt(idx),
                          setValueAs: (v) =>
                            v === "" ? "" : String(v).replace(/\D/g, ""),
                        })}
                        placeholder="Pcs"
                        onBeforeInput={handleIntegerBeforeInput}
                        onKeyDown={handleIntegerKeyDown}
                        onPaste={handleIntegerPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Rate/Pcs
                      </label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupRateByPcs`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) =>
                            v === "" ||
                            DECIMAL_RE.test(String(v)) ||
                            "Only numbers",
                        })}
                        placeholder="Rate/Pcs"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(
                            e,
                            watch(`diamonds.${idx}.diaPopupRateByPcs`) ?? ""
                          )
                        }
                        onPaste={handleDecimalPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Amt */}
                    <div>
                      <label className="text-[10px] text-gray-600">Amt</label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupAmt`)}
                        placeholder="0.00"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>
                  </div>

                  {/* Second line */}
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Deduct
                      </label>
                      <select
                        {...register(`diamonds.${idx}.diaPopupDeduct`)}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300"
                      >
                        <option value="">--Select--</option>
                        <option value="deduct">Deduct</option>
                        <option value="add">Add</option>
                        <option value="nonDeduct">Non-Deduct</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="text-[10px] text-gray-600">
                        Description
                      </label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupDescription`)}
                        placeholder="Description"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    <div className="flex gap-2 justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          const prev = Math.max(0, idx - 1);
                          remove(idx);
                          setTimeout(() => {
                            setFocus(`diamonds.${prev}.diaPopupName`);
                            scrollRowIntoView(prev);
                          }, 150);
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-red-50"
                        title="Remove row"
                      >
                        <FaTrash size={10} className="text-gray-500" />
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const newIndex = fields.length;
                          append(defaultDiamondFields);
                          requestAnimationFrame(() => {
                            setTimeout(() => {
                              setFocus(`diamonds.${newIndex}.diaPopupName`);
                              scrollRowIntoView(newIndex);
                            }, 120);
                          });
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-green-50"
                        title="Add row"
                      >
                        <FaPlus size={10} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Totals chip */}
          <div className="sticky bottom-10 flex justify-center pointer-events-none">
            <div className="bg-violet-100 border border-violet-300 rounded-full text-[11px] px-4 py-1 shadow-md text-violet-700 flex gap-4">
              <span>
                💎 Total Ct: <strong>{totals.totalCt}</strong>
              </span>
              <span>
                🔹 Total Pcs: <strong>{totals.totalPcs}</strong>
              </span>
              <span>
                💰 Total Amt: <strong>{totals.totalAmt}</strong>
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-[#f7f4ff] border-t border-violet-300 px-3 py-2 flex justify-end gap-2 rounded-b-xl">
            <button
              type="button"
              onClick={requestClose}
              className="text-xs px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(doSave)}
              className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
            >
              Save
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InvoiceDiamondPopUp;
